import React, { useState } from 'react';
import {
  PiCheckCircle, PiCaretDown, PiTruck, PiShieldCheck, PiLightning, PiHeart,
} from 'react-icons/pi';
import {
  PreviewFrame,
} from './_shared';

// Turn a pasted video URL into an embeddable player URL (YouTube / Vimeo).
// Returns null for anything else — those play through a native <video> tag instead.
function toVideoEmbedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?[^#]*v=|shorts\/|embed\/|live\/)|youtu\.be\/)([\w-]{6,})/i);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
  return null;
}

export default function VslPitchTemplate({ sections, entity, viewport, theme, editable, onSectionClick }) {
  const warning  = sections.warning  || {};
  const hero     = sections.hero     || {};
  const problem  = sections.problem  || {};
  const includes = sections.includes || {};
  const benefits = sections.benefits || {};
  const offer    = sections.offer    || {};
  const remember = sections.remember || {};
  const faq      = sections.faq      || {};
  const accent = theme?.primaryColor || '#22B364'; // high-converting green
  const danger = '#D9342B';
  const product = entity || {};

  const benefitIcons = { lightning: PiLightning, shield: PiShieldCheck, heart: PiHeart, truck: PiTruck };

  // Video playback + FAQ accordion state (live page is interactive, unlike the admin preview)
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  // Editable-field fallbacks — `??` keeps legacy output for pages saved before these
  // fields existed; an explicit '' (merchant cleared it) hides the element.
  const headlineSuffix   = hero.headlineSuffix ?? ' — Watch this first';
  const videoBadgeText   = hero.videoBadgeText ?? '● LIVE';
  const heroFootnote     = hero.footnote ?? '🔊 Make sure your sound is on';
  const videoUrl         = String(hero.videoUrl || '').trim();
  const videoEmbedUrl    = toVideoEmbedUrl(videoUrl);
  const includesImage    = includes.imageUrl ?? product?.imageUrl;
  const offerSubtext     = offer.subtext ?? 'Limited-time offer — only on this page';
  const compareAtPrefix  = offer.compareAtPrefix ?? 'Normally';
  const saveBadge        = offer.saveBadge ?? (Number(offer.discountPct) > 0 ? `${offer.discountPct}% OFF` : '');
  const priceSubtext     = offer.priceSubtext ?? 'One-time payment · Free shipping';
  const offerTrustItems  = Array.isArray(offer.trustItems) ? offer.trustItems : ['🔒 Secure checkout', '💳 Major cards accepted'];
  const guaranteeText    = offer.guaranteeText ?? '✅ 60-day money-back guarantee';
  const rememberHeadline = remember.headline ?? '⚠ REMEMBER!';

  return (
    <PreviewFrame viewport={viewport} accent={accent} theme={theme} sections={sections} editable={editable} onSectionClick={onSectionClick}>
      {/* PATTERN-INTERRUPT WARNING BAR */}
      {warning.enabled !== false && (
        <div data-lp-section="warning" style={{
          background: '#FFE5E2', color: danger, textAlign: 'center',
          padding: '10px 16px', fontSize: 13, fontWeight: 800, letterSpacing: 0.3,
          borderBottom: `2px solid ${danger}`,
        }}>
          <span data-lp-field="text">{warning.text || '⚠ WARNING: Do not close this page'}</span>
        </div>
      )}

      {/* PROGRESS BAR (visual) */}
      {hero.showProgressBar !== false && (
        <div data-lp-section="hero" style={{ height: 6, background: '#EEE' }}>
          <div style={{ width: '38%', height: '100%', background: accent }} />
        </div>
      )}

      {/* HERO + VIDEO */}
      <section data-lp-section="hero" style={{
        padding: viewport === 'mobile' ? '32px 20px' : '48px 32px',
        textAlign: 'center', background: '#FFFCF2',
      }}>
        <h1 style={{
          fontSize: viewport === 'mobile' ? 26 : 36, fontWeight: 900, margin: '0 0 12px',
          color: '#1F1B33', lineHeight: 1.15, letterSpacing: -0.5,
          maxWidth: 760, marginLeft: 'auto', marginRight: 'auto',
        }}>
          {hero.headline || 'The complete system'}
          {headlineSuffix && <span style={{ color: danger }} data-lp-field="headlineSuffix">{headlineSuffix}</span>}
        </h1>
        <p style={{ fontSize: 15, color: '#4a4458', maxWidth: 600, margin: '0 auto 24px', lineHeight: 1.5 }}>
          {hero.subheadline || 'Watch the short video below to see exactly how it works.'}
        </p>

        {/* Video frame — clicking plays hero.videoUrl when the merchant set one */}
        <div
          onClick={() => { if (videoUrl && !videoPlaying) setVideoPlaying(true); }}
          style={{
            maxWidth: 720, margin: '0 auto', aspectRatio: '16/9',
            background: (hero.videoPosterUrl || hero.heroImageUrl)
              ? `linear-gradient(rgba(0,0,0,0.35),rgba(0,0,0,0.35)), url(${hero.videoPosterUrl || hero.heroImageUrl}) center/cover`
              : '#1F1B33',
            borderRadius: 12, position: 'relative', overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            cursor: (videoUrl && !videoPlaying) ? 'pointer' : 'default',
          }}
        >
          {(videoPlaying && videoUrl) ? (
            videoEmbedUrl ? (
              <iframe
                title="Video"
                src={videoEmbedUrl}
                allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
              />
            ) : (
              <video
                controls
                autoPlay
                src={videoUrl}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
              />
            )
          ) : (
            <>
              <button type="button" style={{
                width: 72, height: 72, borderRadius: '50%', border: 'none',
                background: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              }}>
                <span style={{
                  width: 0, height: 0,
                  borderTop: '14px solid transparent', borderBottom: '14px solid transparent',
                  borderLeft: `22px solid ${danger}`, marginLeft: 6,
                }} />
              </button>
              {videoBadgeText && (
                <div style={{
                  position: 'absolute', bottom: 12, left: 12,
                  background: danger, color: '#fff', fontSize: 11, fontWeight: 800,
                  padding: '4px 8px', borderRadius: 4, letterSpacing: 0.5,
                }} data-lp-field="videoBadgeText">{videoBadgeText}</div>
              )}
            </>
          )}
        </div>
        {heroFootnote && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#7d7a86' }} data-lp-field="footnote">
            {heroFootnote}
          </div>
        )}
      </section>

      {/* PROBLEM / AGITATE */}
      <section data-lp-section="problem" style={{
        background: '#fff', padding: viewport === 'mobile' ? '40px 24px' : '56px 32px',
        borderTop: '1px solid #ECE8F5', borderBottom: '1px solid #ECE8F5',
      }}>
        <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: viewport === 'mobile' ? 22 : 28, fontWeight: 800, margin: '0 0 16px', color: danger, letterSpacing: -0.3 }}>
            {problem.headline || "If you've tried everything and nothing sticks, it's not your fault"}
          </h2>
          <p style={{ fontSize: 16, color: '#4a4458', lineHeight: 1.65, margin: 0 }}>
            {problem.body || 'Most products weren\'t built for real life. We obsessed over the details so you don\'t have to.'}
          </p>
        </div>
      </section>

      {/* WHAT'S INCLUDED — image + checklist */}
      <section data-lp-section="includes" style={{ background: '#F5F2EC', padding: viewport === 'mobile' ? '40px 24px' : '64px 32px' }}>
        <div style={{
          maxWidth: 1000, margin: '0 auto',
          display: 'grid', gap: 32,
          gridTemplateColumns: viewport === 'mobile' ? '1fr' : '1fr 1.2fr',
          alignItems: 'center',
        }}>
          <div style={{
            aspectRatio: '4/3', borderRadius: 8,
            background: includesImage
              ? `url(${includesImage}) center/cover`
              : 'linear-gradient(135deg, #E5E0F2, #C4A8FF)',
            border: '1px solid #ddd', boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
          }} />
          <div>
            <h2 style={{ fontSize: viewport === 'mobile' ? 22 : 28, fontWeight: 800, margin: '0 0 20px', letterSpacing: -0.3, textAlign: 'center' }}>
              {includes.headline || 'Your order includes:'}
            </h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(includes.items || []).map((it, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 15, color: '#1F1B33', lineHeight: 1.5 }}>
                  <PiCheckCircle size={20} weight="fill" style={{ color: accent, flexShrink: 0, marginTop: 2 }} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* BENEFITS GRID — big icons, 4-up */}
      <section data-lp-section="benefits" style={{ padding: viewport === 'mobile' ? '48px 24px' : '72px 32px', background: '#fff', textAlign: 'center' }}>
        <h2 style={{ fontSize: viewport === 'mobile' ? 24 : 32, fontWeight: 800, margin: '0 0 8px', letterSpacing: -0.5, color: '#1F1B33' }}>
          {benefits.headline || 'Here\'s what you get'}
        </h2>
        <div style={{ width: 60, height: 4, background: accent, margin: '0 auto 40px', borderRadius: 2 }} />
        <div style={{
          display: 'grid', gap: 32, maxWidth: 1000, margin: '0 auto',
          gridTemplateColumns: viewport === 'mobile' ? '1fr 1fr' : 'repeat(4, 1fr)',
        }}>
          {(benefits.items || []).map((b, i) => {
            const Icon = benefitIcons[b.icon] || PiCheckCircle;
            return (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%', background: '#FFE5E8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 14px',
                }}>
                  <Icon size={30} style={{ color: danger }} weight="fill" />
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6, color: '#1F1B33' }}>{b.title}</div>
                <div style={{ fontSize: 13, color: '#4a4458', lineHeight: 1.5 }}>{b.body}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* OFFER + PRICING */}
      <section data-lp-section="offer" style={{
        background: 'linear-gradient(180deg, #1F1B33 0%, #2D2849 100%)',
        color: '#fff', padding: viewport === 'mobile' ? '48px 24px' : '72px 32px', textAlign: 'center',
      }}>
        <h2 style={{ fontSize: viewport === 'mobile' ? 26 : 34, fontWeight: 900, margin: '0 0 8px', letterSpacing: -0.5 }}>
          {offer.headline || 'Get it today and save'}
        </h2>
        {offerSubtext && (
          <p style={{ fontSize: 14, opacity: 0.7, margin: '0 0 28px' }} data-lp-field="subtext">{offerSubtext}</p>
        )}

        <div style={{
          maxWidth: 460, margin: '0 auto', background: '#fff', color: '#1F1B33',
          borderRadius: 12, padding: 28, boxShadow: '0 16px 40px rgba(0,0,0,0.4)',
        }}>
          {(offer.compareAtLabel || saveBadge) && (
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 12, marginBottom: 8 }}>
              {offer.compareAtLabel && (
                <span style={{ fontSize: 18, color: '#9892a3', textDecoration: 'line-through', fontWeight: 600 }} data-lp-field="compareAtLabel">
                  {compareAtPrefix && <span data-lp-field="compareAtPrefix">{compareAtPrefix} </span>}{offer.compareAtLabel}
                </span>
              )}
              {saveBadge && (
                <span style={{ background: '#FFE5E8', color: danger, fontSize: 12, fontWeight: 800, padding: '4px 8px', borderRadius: 4 }} data-lp-field="saveBadge">
                  {saveBadge}
                </span>
              )}
            </div>
          )}
          {offer.priceLabel && (
            <div style={{ fontSize: 48, fontWeight: 900, letterSpacing: -1, marginBottom: 4 }} data-lp-field="priceLabel">
              {offer.priceLabel}
            </div>
          )}
          {priceSubtext && (
            <div style={{ fontSize: 13, color: '#7d7a86', marginBottom: 20 }} data-lp-field="priceSubtext">
              {priceSubtext}
            </div>
          )}

          <button type="button" data-lp-field="ctaLabel" style={{
            width: '100%', background: accent, color: '#fff', border: 'none',
            padding: '18px', borderRadius: 8, fontSize: 16, fontWeight: 900,
            cursor: 'pointer', letterSpacing: 0.5, marginBottom: 12,
            boxShadow: '0 6px 18px rgba(34,179,100,0.35)',
          }}>
            {offer.ctaLabel || '▶ YES — I WANT THIS NOW'}
          </button>
          {offerTrustItems.length > 0 && (
            <div style={{ fontSize: 11, color: '#7d7a86', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }} data-lp-field="trustItems">
              {offerTrustItems.map((it, i) => (
                <span key={i}>{it}</span>
              ))}
            </div>
          )}
        </div>

        {guaranteeText && (
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', maxWidth: 520, margin: '24px auto 0', lineHeight: 1.5 }} data-lp-field="guaranteeText">
            {guaranteeText}
          </p>
        )}
      </section>

      {/* REMEMBER yellow callout */}
      {remember.enabled !== false && (
        <section data-lp-section="remember" style={{ padding: viewport === 'mobile' ? '32px 24px' : '48px 32px', background: '#fff' }}>
          <div style={{
            maxWidth: 720, margin: '0 auto',
            background: '#FFF6E0', border: `2px dashed #E5B400`,
            borderRadius: 8, padding: 24, textAlign: 'center',
          }}>
            {rememberHeadline && (
              <div style={{ fontSize: 14, fontWeight: 900, color: '#8a6500', letterSpacing: 1.5, marginBottom: 8 }} data-lp-field="headline">
                {rememberHeadline}
              </div>
            )}
            <p style={{ fontSize: 14, color: '#5c4400', lineHeight: 1.55, margin: 0, fontWeight: 600, fontStyle: 'italic' }} data-lp-field="body">
              {remember.body || 'This special discount is only available on this page.'}
            </p>
          </div>
        </section>
      )}

      {/* FAQ */}
      {(() => {
        const faqItems = (faq.items || []).filter((it) => (it.question || '').trim() && (it.answer || '').trim());
        if (faqItems.length === 0) return null;
        return (
          <section data-lp-section="faq" style={{ padding: viewport === 'mobile' ? '40px 24px' : '64px 32px', background: '#FAFAFC' }}>
            <div style={{ maxWidth: 720, margin: '0 auto' }}>
              <h2 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 24px', textAlign: 'center', letterSpacing: -0.3 }}>
                {faq.headline || 'Still have questions?'}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {faqItems.map((item, i) => {
                  const open = openFaq === i;
                  return (
                    <div key={i} style={{ background: '#fff', border: '1px solid #ECE8F5', borderRadius: 8, padding: 18 }}>
                      <div
                        role="button"
                        tabIndex={0}
                        aria-expanded={open}
                        onClick={() => setOpenFaq(open ? -1 : i)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenFaq(open ? -1 : i); } }}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                      >
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#1F1B33' }}>{item.question}</span>
                        <PiCaretDown size={16} style={{ color: '#7d7a86', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
                      </div>
                      {open && (
                        <p style={{ fontSize: 13, color: '#4a4458', margin: '10px 0 0', lineHeight: 1.55 }}>{item.answer}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })()}
    </PreviewFrame>
  );
}

